import { useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { FREE_PLUGIN_IDS, getPluginCatalogEntry } from '@busswerks/shared'
import type { LicenseKind } from '@busswerks/shared'

/** Pure entitlement resolver. Given an auth payload + plugin id,
 *  return the license the user holds (`null` = locked). Exported
 *  so callers that already have an auth subscription can resolve
 *  many ids at once without spinning up one hook per id. */
export function resolvePluginEntitlement(
  authUser: LegacyTierEntitlements | null | undefined,
  pluginId: string | null | undefined,
): PluginEntitlement {
  if (!pluginId) return null
  if (FREE_PLUGIN_IDS.has(pluginId)) return 'free'
  const entry = getPluginCatalogEntry(pluginId)
  if (!entry) return 'free'
  if (entry.free) return 'free'

  const addOns = authUser?.addOnEntitlements ?? []
  const owned = addOns.find(a => a.id === pluginId)
  if (owned) return owned.license

  const tier = authUser?.tier
  if (tier === 'pro' || tier === 'studio') return 'personal'
  return null
}

/**
 * Per-module entitlement resolution.
 *
 * Returns the license type the current user holds for a given
 * plugin id, or `null` if they don't own it. Free modules always
 * resolve as `'free'`.
 *
 * Bridge logic during Wave 7 (before the per-module Stripe catalog
 * ships):
 *
 * - Catalog `free: true` entries → always `'free'`.
 * - Unknown plugin ids (not in the catalog) → always `'free'` to
 *   avoid accidentally locking out registry plugins that predate
 *   the Store.
 * - `subscription.addOnEntitlements` (new field; may not be
 *   populated yet on the server) → honored when present.
 * - Fallback to existing tier: `tier === 'pro' | 'studio'` grants
 *   every paid module at `'personal'` level so existing
 *   subscribers keep access while the per-module billing migration
 *   is in flight. Free-tier users see paid modules locked.
 *
 * Follow-up PR (Wave 7b) replaces the tier fallback with the
 * explicit `addOnEntitlements` array once checkout + webhook
 * writes it.
 */
export type PluginEntitlement = 'free' | LicenseKind | null

interface LegacyTierEntitlements {
  tier?: string
  addOnEntitlements?: Array<{ id: string; license: LicenseKind }>
}

export function usePluginEntitlement(
  pluginId: string | null | undefined,
): PluginEntitlement {
  const { user } = useAuth()
  return useMemo(
    () =>
      resolvePluginEntitlement(user as LegacyTierEntitlements | null, pluginId),
    [pluginId, user],
  )
}

/** Batch resolver — handy when a single component needs the
 *  entitlement for many plugin ids at once (e.g. the pad editor
 *  rendering dropdown options). Returns a `Map<id, entitlement>`
 *  that tracks the current user's subscription. */
export function usePluginEntitlementMap(): (
  id: string | null | undefined,
) => PluginEntitlement {
  const { user } = useAuth()
  return useMemo(() => {
    const authUser = user as LegacyTierEntitlements | null
    return (id: string | null | undefined) =>
      resolvePluginEntitlement(authUser, id)
  }, [user])
}

/** Convenience — true when the user can use the plugin, false
 *  when they need to purchase. */
export function usePluginOwned(pluginId: string | null | undefined): boolean {
  return usePluginEntitlement(pluginId) !== null
}
